'use client';

import {cn} from '@/lib/utils';
import moment from 'moment';
import {useTranslations} from 'next-intl';
import SliderBase from './SliderBase';
import Url from './Url';

export default function Slider(Prop: {moduleOption: any; moduleData?: any}) {
  const options = Prop.moduleOption || {};
  // sliderSetting
  const sliderItems = Prop.moduleData?.items ?? options.items ?? [];
  const imageHeight = options?.height || 120;
  const isCustomTemplate = (options?.template ?? '') != '';

  const tDate = useTranslations('Date');

  // Extensible formatting helper registry. Add new helpers here.
  const dateHandler = (val: any, format: string) => {
    if (!val) return '';
    const date = moment(val);
    if (!date.isValid()) return String(val);

    if (tDate) {
      const weekday = {
        Monday: tDate('Monday'),
        Tuesday: tDate('Tuesday'),
        Wednesday: tDate('Wednesday'),
        Thursday: tDate('Thursday'),
        Friday: tDate('Friday'),
        Saturday: tDate('Saturday'),
        Sunday: tDate('Sunday')
      };
      let formatted = date.format(format);
      const englishWeekday = date.format('dddd');
      if (weekday[englishWeekday as keyof typeof weekday]) {
        formatted = formatted.replace(
          englishWeekday,
          weekday[englishWeekday as keyof typeof weekday]
        );
      }
      return formatted;
    }
    return date.format(format);
  };

  const helpers: Record<string, (val: any) => string> = {
    date: (val: any) => {
      return dateHandler(val, tDate('dateFormat') || 'YYYY-MM-DD');
    },
    postAt: (val: any) => {
      return dateHandler(val, tDate('dateFormatLong') || 'YYYY-MM-DD');
    },
    uppercase: (val: any) => {
      return String(val ?? '').toUpperCase();
    },
    lowercase: (val: any) => {
      return String(val ?? '').toLowerCase();
    }
  };

  const formTemplate = (templateItem: any) => {
    let template = options?.template ?? '';

    // Regex to match optional helper/prefix and key path, e.g. {date:posted_at} or {thumbnail.url}
    const regex =
      /\{(?:([A-Za-z0-9_]+):)?([A-Za-z0-9_]+(?:\.[A-Za-z0-9_]+)*)\}/g;
    const matches = Array.from(template.matchAll(regex));
    for (const match of matches as RegExpExecArray[]) {
      const fullMatch = match[0];
      const prefix = match[1];
      const keyPath = match[2];

      // get data with dot path
      let data = templateItem;
      keyPath.split('.').forEach((k) => {
        if (!data) return;
        data = data?.[k.trim()];
      });

      // Apply helper if prefix is provided and helper exists
      let formattedData = data;
      if (prefix && helpers[prefix]) {
        formattedData = helpers[prefix](data);
      }

      const displayValue =
        formattedData !== undefined && formattedData !== null
          ? String(formattedData)
          : '';
      template = template.replaceAll(fullMatch, displayValue).replace('\n', '');
    }
    return template;
  };

  return (
    <SliderBase
      options={options}
      template={(sliderItem: any) => {
        return (
          <Url href={sliderItem?.url || '#'} title={sliderItem.caption || ''}>
            {isCustomTemplate && (
              <div
                dangerouslySetInnerHTML={{__html: formTemplate(sliderItem)}}
              ></div>
            )}
            {!isCustomTemplate && (
              <div className="flex flex-col items-start justify-center hover:bg-[#f8f8f8] rounded-b-sm ">
                {sliderItem.image && (
                  <div className="w-full flex justify-center items-start">
                    <img
                      className={cn(
                        imageHeight < 0
                          ? 'object-contain w-full h-auto max-w-[100%]'
                          : 'object-cover object-center w-[100%]'
                      )}
                      src={sliderItem.image}
                      alt={`${sliderItem.caption || ''} image`}
                      style={{
                        ...(imageHeight > 0 ? {height: imageHeight + 'px'} : {})
                      }}
                    />
                  </div>
                )}

                {sliderItem.caption && (
                  <div
                    className={cn(
                      'h-[70px] px-4 lg:px-2 py-3 container flex items-start justify-center md:justify-start',
                      'text-[13px] leading-relaxed font-500  overflow-hidden'
                    )}
                  >
                    <div className="line-clamp-2">{sliderItem.caption}</div>
                  </div>
                )}
              </div>
            )}
          </Url>
        );
      }}
      items={sliderItems}
    />
  );
}
